import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { getDiningOptions, DiningOption } from '../utils/dining';
import { calculateDistance, formatDistance } from '../utils/distance';
import { parseDateInCST } from '../utils/dateUtils';
import { cleanText } from '../utils/textUtils';
import { useAuth } from '../contexts/AuthContext';
import { useUserLocation } from '../hooks/useUserLocation';
import { useUserPreferences } from '../hooks/useUserPreferences';
import FavoriteButton from '../components/FavoriteButton';
import ShareButton from '../components/ShareButton';
import './EventDetail.css';

interface Event {
  id: string;
  slug: string;
  title: string;
  description: string;
  short_description: string;
  category: string;
  subcategory?: string;
  tags?: string[];
  age_min: number;
  age_max: number;
  age_description?: string;
  venue_name: string;
  address: string;
  city: string;
  state: string;
  zip_code?: string;
  latitude: number;
  longitude: number;
  start_date: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  duration_minutes?: number;
  is_free: boolean;
  price_min?: number;
  price_max?: number;
  price_description?: string;
  requires_registration: boolean;
  registration_url?: string;
  is_indoor: boolean;
  is_outdoor: boolean;
  weather_dependent: boolean;
  parking_type?: string;
  parking_cost?: number;
  parking_notes?: string;
  is_wheelchair_accessible: boolean;
  is_stroller_friendly: boolean;
  has_restrooms: boolean;
  has_food: boolean;
  food_allowed: boolean;
  rating_avg?: number;
  rating_count?: number;
  source: string;
}

const EventDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { preferences } = useUserPreferences();
  const [selectedCity, setSelectedCity] = useState<string>('Houston');
  const { location: userLocation } = useUserLocation(selectedCity, { requestGeolocation: false });
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [diningOptions, setDiningOptions] = useState<DiningOption[]>([]);
  const [loadingDining, setLoadingDining] = useState(false);
  const [showDining, setShowDining] = useState(false);
  const [showParking, setShowParking] = useState(false);

  useEffect(() => {
    if (preferences?.city) setSelectedCity(preferences.city);
    else {
      const savedCity = localStorage.getItem('selectedCity');
      if (savedCity) setSelectedCity(savedCity);
    }
  }, [preferences]);

  useEffect(() => {
    if (slug) fetchEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (event) fetchDiningOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      setError(null);
      const today = new Date().toISOString().split('T')[0];
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('slug', slug)
        .eq('status', 'approved')
        .or(`end_date.is.null,end_date.gte.${today}`)
        .single();
      if (error) throw error;
      if (!data) throw new Error('Event not found');
      setEvent(data);
    } catch (err: any) {
      console.error('Error fetching event:', err);
      setError(err.message || 'Event not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchDiningOptions = async () => {
    if (!event) return;
    try {
      setLoadingDining(true);
      const options = await getDiningOptions(event.city, event.latitude, event.longitude);
      setDiningOptions(options);
    } catch (err) {
      console.error('Error fetching dining options:', err);
    } finally {
      setLoadingDining(false);
    }
  };

  const getParkingOptions = () => {
    if (!event) return [];
    const { latitude, longitude, venue_name } = event;
    return [
      { name: 'Parking Garages', description: 'Multi-level covered parking structures', searchUrl: `https://www.google.com/maps/search/parking+garage/@${latitude},${longitude},15z`, type: 'Covered Parking' },
      { name: 'Parking Lots', description: 'Open-air surface parking areas', searchUrl: `https://www.google.com/maps/search/parking+lot/@${latitude},${longitude},15z`, type: 'Surface Parking' },
      { name: 'All Nearby Parking', description: `Parking options near ${venue_name}`, searchUrl: `https://www.google.com/maps/search/parking/@${latitude},${longitude},16z`, type: 'All Types' }
    ];
  };

  const getCategoryEmoji = (category: string) => {
    const emojiMap: { [key: string]: string } = { 'Arts': '🎨', 'Sports': '⚽', 'STEM': '🔬', 'Outdoors': '🌳', 'Performing Arts': '🎭', 'Library': '📚', 'Music': '🎵' };
    return emojiMap[category] || '🎉';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Date TBD';
    try {
      const date = parseDateInCST(dateString);
      if (isNaN(date.getTime())) return 'Date TBD';
      return date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Chicago' });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Date TBD';
    }
  };
  
  const formatTime = (dateString: string) => {
    // If we have explicit start_time and end_time fields, use those
    if (event?.start_time) {
      try {
        const timeOptions: Intl.DateTimeFormatOptions = {
          hour: 'numeric',
          minute: '2-digit',
          timeZone: 'America/Chicago'
        };
        const startTimeStr = new Date(`2000-01-01T${event.start_time}`).toLocaleTimeString('en-US', timeOptions);
        if (event.end_time) {
          const endTimeStr = new Date(`2000-01-01T${event.end_time}`).toLocaleTimeString('en-US', timeOptions);
          return `${startTimeStr} - ${endTimeStr} CST`;
        }
        return `${startTimeStr} CST`;
      } catch (error) {
        console.error('Error formatting time:', error);
      }
    }
    
    // Fallback to using start_date
    if (!dateString) return 'Time TBD';
    try {
      const date = parseDateInCST(dateString);
      if (isNaN(date.getTime())) return 'Time TBD';
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago', timeZoneName: 'short' });
    } catch (error) {
      console.error('Error formatting time:', dateString, error);
      return 'Time TBD';
    }
  };

  const getDistanceToEvent = (): string | null => {
    if (!user || !userLocation || !event) return null;
    const distance = calculateDistance(userLocation.lat, userLocation.lon, event.latitude, event.longitude);
    return formatDistance(distance);
  };

  if (loading) return <div className="event-detail-container"><div className="loading"><div className="spinner"></div><p>Loading event...</p></div></div>;
  if (error || !event) return <div className="event-detail-container"><div className="error"><h2>😕 Event Not Found</h2><p>{error || 'This event could not be found.'}</p><button onClick={() => navigate('/')} className="back-btn">← Back to Events</button></div></div>;

  return (
    <div className="event-detail-container">
      <button onClick={() => navigate('/')} className="back-button">← Back to All Events</button>
      <div className="event-detail-content">
        <div className="event-header">
          <div className="event-action-buttons">
            <FavoriteButton itemId={event.id} itemType="event" size="large" />
            <ShareButton 
              title={event.title}
              description={event.short_description}
              url={`${window.location.origin}/event/${event.slug}`}
              date={formatDate(event.start_date)}
              location={`${event.venue_name}, ${event.city}`}
              size="large"
            />
          </div>
          <div className="category-badge"><span className="badge-emoji">{getCategoryEmoji(event.category)}</span><span className="badge-text">{event.category}</span></div>
          <h1 className="event-title">{event.title}</h1>
          {event.rating_avg && event.rating_count && <div className="rating-display"><span className="stars">⭐</span><span className="rating-value">{event.rating_avg.toFixed(1)}</span><span className="rating-count">({event.rating_count} reviews)</span></div>}
        </div>
        <div className="event-body">
          <div className="info-section"><h2 className="section-title">📅 When</h2><div className="info-content"><p className="date-display">{formatDate(event.start_date)}</p><p className="time-display">{formatTime(event.start_date)}</p>{event.duration_minutes && <p className="duration">Duration: {Math.floor(event.duration_minutes / 60)} hours</p>}</div></div>
          <div className="info-section"><h2 className="section-title">👶 Ages</h2><div className="info-content"><p>Ages {event.age_min}-{event.age_max}</p>{event.age_description && <p className="age-note">{event.age_description}</p>}</div></div>
          <div className="info-section"><h2 className="section-title">💵 Cost</h2><div className="info-content">{event.is_free ? <p className="free-badge">FREE!</p> : <>{event.price_min && event.price_max && <p>${event.price_min} - ${event.price_max}</p>}{event.price_description && <p>{event.price_description}</p>}</>}</div></div>
          <div className="info-section full-width"><h2 className="section-title">📝 About This Event</h2><div className="info-content"><p className="description">{cleanText(event.description || event.short_description)}</p></div></div>
          <div className="info-section full-width"><h2 className="section-title">📍 Location</h2><div className="info-content"><a 
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                event.address || `${event.venue_name}, ${event.city}, ${event.state}`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="location-link address-link"
            >📍 {event.venue_name}</a>{getDistanceToEvent() && <p className="distance-display">🎯 <strong>{getDistanceToEvent()}</strong> from you</p>}</div></div>
          <div className="info-section"><h2 className="section-title">ℹ️ Good to Know</h2><div className="info-content amenities-list">{event.is_indoor && <div className="amenity">✓ Indoor</div>}{event.is_outdoor && <div className="amenity">✓ Outdoor</div>}{event.is_wheelchair_accessible && <div className="amenity">✓ Wheelchair accessible</div>}{event.is_stroller_friendly && <div className="amenity">✓ Stroller-friendly</div>}{event.has_restrooms && <div className="amenity">✓ Restrooms available</div>}{event.has_food && <div className="amenity">✓ Food available</div>}{event.food_allowed && <div className="amenity">✓ Outside food allowed</div>}</div></div>
          <div className="info-section full-width collapsible-section">
            <div className="collapsible-header" onClick={() => setShowParking(!showParking)}><h2 className="section-title">🅿️ Nearby Parking Locations</h2><span className="toggle-icon">{showParking ? '▼' : '▶'}</span></div>
            {showParking && <><p className="section-subtitle">Find and reserve parking near this event</p><div className="parking-options">{getParkingOptions().map((option, index) => (<div key={index} className="parking-card"><div className="parking-header"><h3 className="parking-name">{option.name}</h3><span className="parking-type">{option.type}</span></div><p className="parking-description">{option.description}</p><a href={option.searchUrl} target="_blank" rel="noopener noreferrer" className="parking-link">View on Google Maps →</a></div>))}</div></>}
          </div>
          <div className="info-section full-width collapsible-section">
            <div className="collapsible-header" onClick={() => { setShowDining(!showDining); if (!showDining && diningOptions.length === 0) fetchDiningOptions(); }}><h2 className="section-title">🍽️ Nearby Dining Options</h2><span className="toggle-icon">{showDining ? '▼' : '▶'}</span></div>
            {showDining && <><p className="section-subtitle">Great restaurants near this event</p>{loadingDining ? <div className="loading-dining"><div className="spinner-small"></div><p>Finding nearby restaurants...</p></div> : diningOptions.length > 0 ? <div className="dining-options">{diningOptions.map((option, index) => (<div key={index} className="dining-card"><div className="dining-header"><h3 className="dining-name">{option.name}</h3><span className="dining-cuisine">{option.cuisine}</span></div><p className="dining-description">{option.description}</p>{option.address && <p className="dining-address">📍 {option.address}</p>}<a href={option.searchUrl} target="_blank" rel="noopener noreferrer" className="dining-link">View on Google Maps →</a></div>))}</div> : <p className="no-dining">No restaurants found nearby.</p>}</>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetail;
